import type { ChangeEvent, FC } from 'react'
import { useState } from 'react'

import { config } from '@skytech/config'
import { InfoBox, Input, MessagePopup, Select, Textarea } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { DECIMAL_PATTERN, MAX_DECIMAL_12_4, MIN_PRICE, trimLeadingZeros } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field } from 'formik'
import AlertCircleOutlineIcon from 'mdi-react/AlertCircleOutlineIcon'
import CalculatorVariantOutlineIcon from 'mdi-react/CalculatorVariantOutlineIcon'
import PailOutlineIcon from 'mdi-react/PailOutlineIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import StoreRemoveIcon from 'mdi-react/StoreRemoveIcon'
import { useParams, useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useProfile } from '@/hooks/user'
import queries from '@/modules/admin/queries'
import adminQueries from '@/modules/admin/queries'
import type { AssetExtended } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { leasingResources } from '@/resources/leasing'
import type { Policy } from '@/types'
import { APP_ROUTES } from '@/util/app-routes'

import type { FormFields } from '../types'

import { getSelectOptions } from '../../helpers'

const isDevEnv = config.ENVIRONMENT === 'dev'

// The only way to check the ownership type
const OWNED_TYPE_ID = -1

export const Asset: FC<{
  assetDetails: AssetExtended
  activePolicies: Policy[]
  onSetFormValues: (updatedValues: Partial<FormFields>) => void
  onChangeUserTypeId: (userTypeId: number) => void
}> = ({ activePolicies, assetDetails, onSetFormValues, onChangeUserTypeId }) => {
  const translate = useTranslation()
  const params = useParams()
  const profile = useProfile()
  const assetId = Number(params.id)

  if (isNaN(assetId)) throw Error(translate('Asset ID is not a number!'))
  if (!profile) throw new Error('No profile found for Asset!')

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isAddAssetToTradeInPopupOpen, setIsAddAssetToTradeInPopupOpen] = useState(false)
  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  // TODO: enable for production
  const { data: leasingInfo } = useQuery(
    queries.adminLeasingAssetDetails(assetId),
    () => leasingResources.getLeasingAssetDetails(assetId),
    { enabled: isDevEnv && Boolean(assetDetails) && assetDetails.isLeased }
  )

  const sendPriceRequestMutation = useMutation(adminResources.sendTradeInPriceRequest, {
    onSuccess() {
      setIsAddAssetToTradeInPopupOpen(false)
      queryClient.invalidateQueries(
        adminQueries.adminAssetTradeInOrder(assetDetails.assetGuid ?? '')
      )
    },
  })

  function handleAddToGreenpool() {
    const statusId = assetTypes?.statuses.find(status => status.status === 'AVAILABLE')?.id
    const userTypeId = assetTypes?.userTypes.find(type => type.type === 'GREENPOOL')?.id
    if (!statusId) throw Error('Status id not found!')
    if (!userTypeId) throw Error('User type id not found!')
    onSetFormValues({ statusId, userTypeId })
    onChangeUserTypeId(userTypeId)
  }

  function handleAddToPool() {
    const statusId = assetTypes?.statuses.find(status => status.status === 'AVAILABLE')?.id
    const userTypeId = assetTypes?.userTypes.find(type => type.type === 'POOL')?.id
    if (!statusId) throw Error('Status id not found!')
    if (!userTypeId) throw Error('User type id not found!')
    onSetFormValues({ statusId, userTypeId })
    onChangeUserTypeId(userTypeId)
  }

  const handleCostChange = (e: ChangeEvent<HTMLInputElement>, form: FormikProps<FormFields>) => {
    let newValue = e.target.value.trim()
    if (!DECIMAL_PATTERN.test(newValue) && newValue !== '') return
    form.setFieldValue('cost', newValue)
  }

  const isEcomPoliciesAvailable = activePolicies.length > 0
  const lockAssetType =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetType || leasingInfo?.lockFields?.includes('typeId'))
  const lockAssetDescription =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetDescription ||
      leasingInfo?.lockFields?.includes('assetDescription'))
  const lockAssetOwnership =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.assetOwnership ||
      leasingInfo?.lockFields?.includes('ownershipId'))
  const lockAssetImeiSerialNumber =
    assetDetails.isLeased &&
    (leasingInfo?.leasingFieldLocks?.imeiSerialNumber ||
      leasingInfo?.lockFields?.includes('imeiSerialNumber'))
  const hasNoCost = assetDetails.cost === 0 || assetDetails.cost === null

  // Trade-in
  const isTradeInEnabled = Boolean(profile.featureAccess.tradein)
  const { data: featuresList } = useQuery(queries.adminFeatures(), adminResources.getFeatureList, {
    enabled: isTradeInEnabled,
  })
  const tradeInFeature = featuresList?.find(feature => feature.name === 'tradein')
  const customerFeatures = tradeInFeature?.customerFeatures.find(
    feature => feature.customerId === assetDetails.customerId
  )
  const isTradeInFutureAvailableForCustomer = Boolean(customerFeatures)

  const { data: tradeInOrder, isLoading: isTradeInOrderLoading } = useQuery(
    adminQueries.adminAssetTradeInOrder(assetDetails.assetGuid ?? ''),
    () => {
      if (!assetDetails.assetGuid) throw Error('Asset guid missing!')
      return adminResources.getTradeInAssetOrder(assetDetails.assetGuid)
    },
    { enabled: isTradeInEnabled && Boolean(tradeInFeature) }
  )

  const isAssetOwned = assetDetails.ownershipTypeId === OWNED_TYPE_ID
  const isTradInBtnEnabled = isAssetOwned && isTradeInEnabled

  // Checks if an order has been rejected to make a new request. The newest oder is first.
  const isOrderRejected = Boolean(
    tradeInOrder && tradeInOrder.length > 0 && tradeInOrder[0].statusCode === 'REJECTED'
  )
  const isTradeInProcessOngoing =
    Boolean(tradeInOrder && tradeInOrder.length > 0) && !isOrderRejected

  function handleInitiateTradeIn() {
    if (!isTradeInFutureAvailableForCustomer) {
      const searchParams = new URLSearchParams()
      searchParams.set('selectedFeatureId', String(tradeInFeature?.id))
      searchParams.set('selectedCustomerId', String(assetDetails?.customerId))
      const search = String(searchParams) ? `?${searchParams}` : ''
      navigate(`${APP_ROUTES.ADMIN_FEATURES}${search}`)
      return
    }

    if (!isTradeInProcessOngoing) {
      setIsAddAssetToTradeInPopupOpen(true)
      return
    }

    if (!tradeInOrder?.[0]) throw Error('Trade in order missing!')

    navigate(`${APP_ROUTES.HARDWARE_TRADE_IN}?status=ALL`)
  }

  return (
    <div className="form-column-set">
      <div className="form-column">
        <Field name="assetDescription">
          {({ field, meta }: FieldProps) => (
            <Input
              capitalizeLabel
              isFullWidth
              id="assetDescription"
              className="description-input"
              ariaLabel={translate('Description')}
              label={
                <>
                  <Translation id="Description" />*
                </>
              }
              isDisabled={lockAssetDescription}
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
            />
          )}
        </Field>

        <div className="flex items-center gap-1">
          <Field name="ownershipTypeId">
            {({ field, form }: FieldProps) => (
              <Select
                capitalizeLabel
                id="ownershipTypeId"
                label={
                  <>
                    <Translation id="Ownership" />*
                  </>
                }
                options={
                  getSelectOptions(
                    assetTypes?.ownerships.map(({ id, textId, name }) => ({
                      id,
                      label: name ? { id: textId, values: { name } } : textId,
                    })),
                    translate
                  ) || []
                }
                isDisabled={lockAssetOwnership}
                marginBottom={16}
                {...field}
                onChange={option => {
                  if (lockAssetOwnership) return
                  form.setFieldValue(field.name, option?.value)
                }}
              />
            )}
          </Field>

          <Field name="typeId">
            {({ field, form }: FieldProps) => (
              <Select
                capitalizeLabel
                className="grow"
                id="typeId"
                label={
                  <>
                    <Translation id="Type" />*
                  </>
                }
                options={
                  assetTypes?.types.map(({ id, type }) => ({
                    value: id,
                    label: translate(`asset_type/${type}`),
                  })) || []
                }
                isDisabled={lockAssetType}
                marginBottom={16}
                {...field}
                onChange={option => {
                  if (lockAssetType) return
                  form.setFieldValue(field.name, option?.value)
                }}
              />
            )}
          </Field>
        </div>

        <Field name="ecomPolicyId">
          {({ field, form, meta }: FieldProps) => (
            <Select
              capitalizeLabel
              id="ecomPolicyId"
              label={
                <>
                  <Translation id="Ecom policy" />
                  {isEcomPoliciesAvailable && '*'}
                </>
              }
              options={activePolicies.map(policy => ({
                value: policy.id,
                label: policy.name,
              }))}
              marginBottom={16}
              touched={meta.touched}
              error={meta.error}
              {...field}
              onChange={option => form.setFieldValue(field.name, option?.value)}
            />
          )}
        </Field>
      </div>

      <div className="form-column">
        <div className="two-fields flex flex-wrap items-center gap-1">
          <Field name="imei">
            {({ field, meta }: FieldProps) => (
              <Input
                capitalizeLabel
                isFullWidth
                id="imei"
                ariaLabel={translate('IMEI')}
                label={<Translation id="IMEI" />}
                isDisabled={lockAssetImeiSerialNumber}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>
          <Field name="snr">
            {({ field, meta }: FieldProps) => (
              <Input
                capitalizeLabel
                isFullWidth
                id="snr"
                ariaLabel={translate('Serialnbr')}
                label={<Translation id="Serialnbr" />}
                isDisabled={lockAssetImeiSerialNumber}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>
        </div>

        {hasNoCost && (
          <InfoBox marginBottom={16} emphasis="warning">
            <div className="flex items-center gap-1">
              <AlertCircleOutlineIcon />
              <h3 className="m-0">
                <Translation id="NB! Please add the cost." className="!font-bold" />
              </h3>
            </div>
          </InfoBox>
        )}

        <div className="flex gap-1">
          <Field name="received">
            {({ field, meta }: FieldProps) => (
              <Input
                capitalizeLabel
                isFullWidth
                id="received"
                type="date"
                ariaLabel={translate('Received')}
                label={<Translation id="Received" />}
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                {...field}
              />
            )}
          </Field>

          <Field name="cost">
            {({ field, meta, form }: FieldProps) => (
              <div className="flex gap-1">
                <Input
                  capitalizeLabel
                  isFullWidth
                  id="cost"
                  type="text"
                  inputMode="decimal"
                  min={String(MIN_PRICE)}
                  max={String(MAX_DECIMAL_12_4)}
                  LeftElement={<span>{profile?.currency}</span>}
                  ariaLabel={translate('Cost')}
                  label={
                    <>
                      <Translation id="Cost" />*
                    </>
                  }
                  marginBottom={16}
                  touched={meta.touched}
                  error={meta.error}
                  {...field}
                  value={trimLeadingZeros(field.value)}
                  onChange={e => handleCostChange(e, form)}
                />

                {assetDetails.priceBuyout != null && !assetDetails.isLeased && (
                  <Translation
                    id="Buyout price: {price}"
                    className="mt-7"
                    price={<FormattedNumberWithCurrency amount={assetDetails.priceBuyout || 0} />}
                  />
                )}
              </div>
            )}
          </Field>
        </div>
        <div className="w-full">
          <Field name="conditionDesc">
            {({ field, meta }: FieldProps) => (
              <Textarea
                className="resize-y"
                capitalizeLabel
                isFullWidth
                id="conditionDesc"
                marginBottom={16}
                touched={meta.touched}
                error={meta.error}
                maxLength={250}
                label={<Translation id="Condition of the asset (visible in GreenPool)" />}
                // Hack to calculate the number of rows based on the length of the value
                rows={Math.floor(field.value.length / 60) + 1}
                {...field}
              />
            )}
          </Field>
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-1">
          <Button
            className="m-0 flex h-auto flex-col justify-center gap-1 px-3 py-4"
            Icon={<RecycleIcon />}
            onClick={handleAddToGreenpool}
          >
            <Translation id="Add to greenpool" className="text-wrap" />
          </Button>
          <Button
            className="m-0 flex h-auto flex-col justify-center gap-1 px-3 py-4"
            Icon={<PailOutlineIcon />}
            onClick={handleAddToPool}
          >
            <Translation id="Add to pool" />
          </Button>
          <Button
            disabled
            className="m-0 flex h-auto flex-col justify-center gap-1 px-3 py-4"
            Icon={<StoreRemoveIcon />}
          >
            <Translation id="Sell" />
          </Button>

          {isTradInBtnEnabled && (
            <Button
              className="m-0 flex h-auto flex-col justify-center gap-1 px-3 py-4"
              isLoading={isTradeInOrderLoading}
              Icon={<CalculatorVariantOutlineIcon />}
              onClick={handleInitiateTradeIn}
            >
              <Translation id={isTradeInProcessOngoing ? 'Trade-in order' : 'Request price'} />
            </Button>
          )}

          <MessagePopup
            isOpen={isAddAssetToTradeInPopupOpen}
            title={<Translation id="Add asset" />}
            message={<span>This is text about adding asset to the tradein order.</span>}
          >
            <footer className="flex justify-end">
              <Button variant="tertiary" onClick={() => setIsAddAssetToTradeInPopupOpen(false)}>
                <Translation id="Cancel" />
              </Button>
              <Button
                isLoading={sendPriceRequestMutation.isLoading}
                onClick={() => {
                  if (!assetDetails.assetGuid) throw Error('Missing asset guid!')
                  sendPriceRequestMutation.mutate({
                    assetId: assetDetails.assetGuid,
                  })
                }}
              >
                <Translation id="Send request" />
              </Button>
            </footer>
          </MessagePopup>
        </div>
      </div>
    </div>
  )
}
